import React from 'react';
import { useIntl } from 'react-intl';
import type { Organizer } from '@/types';
import { OrganizerAvatar } from '@/components/OrganizerCard/OrganizerData/OrganizerAvatar/OrganizerAvatar';
import { FollowButton, FollowButtonVariant } from '@/components/_shared/buttons/FollowButton/FollowButton';
import styles from './OrganizerInfo.module.scss';
import { LISTING_HEAP_PAGE_AREA_IDS } from '@/components/_shared/constants';

type OrganizerInfoProps = {
  organizer?: Organizer;
  /** When false, the follow button is hidden (e.g. on password gate). Default true. */
  showFollowButton?: boolean;
  /** When set, used as the organizer name link href instead of "#organizer-card". */
  organizerLinkHref?: string;
  /** When true, avoids falling back to "#organizer-card" when no organizerLinkHref is provided. */
  suppressDefaultLinkFallback?: boolean;
};

export const OrganizerInfo: React.FC<OrganizerInfoProps> = ({
  organizer,
  showFollowButton = true,
  organizerLinkHref,
  suppressDefaultLinkFallback = false,
}) => {
  const { formatMessage } = useIntl();

  if (!organizer || !organizer.name) {
    return;
  }

  const linkHref = organizerLinkHref ?? (suppressDefaultLinkFallback ? undefined : '#organizer-card');

  return (
    <div className={styles.wrapper} data-testid="organizer-info">
      <div className={styles.organizerInfoContent}>
        <div className={styles.organizerAvatarWrapper}>
          <OrganizerAvatar organizer={organizer} size="small" />
        </div>
        <span className={styles.byTag}>
          {formatMessage({ id: 'By', defaultMessage: 'By', description: 'By tag for organizer info' })}
        </span>
        {linkHref ? (
          <a className={styles.organizerName} href={linkHref}>
            {organizer.name}
          </a>
        ) : (
          <span className={styles.organizerName}>{organizer.name}</span>
        )}
      </div>
      {showFollowButton && (
        <div className={styles.followButtonWrapper}>
          <FollowButton
            organizer={organizer}
            variant={FollowButtonVariant.Organizer}
            pageArea={LISTING_HEAP_PAGE_AREA_IDS.SIMPLIFIED_ORGANIZER}
          />
        </div>
      )}
    </div>
  );
};
